import { defineComponent } from 'vue';
import utils from '../../Shared/utils.jsx';
import BaseComponent from './BaseComponentMixin.jsx';
import { appSettings } from '@/Shared/appSettings.js';
import { normalizeIconName } from '../../Shared/buttonAppearance.js';
import { QSelect, QItem, QItemSection, QItemLabel, QAvatar } from 'quasar';
import TranslationContainer from './TranslationContainer.jsx';
import './css/AutoComplete.css';

export default defineComponent({
    name: 'auto-complete',
    mixins: [BaseComponent],
    components: {
        QSelect, QItem, QItemSection, QItemLabel, QAvatar,
        TranslationContainer
    },
    data: function () {
        return {
            phrenderer: null,
            Value: "",
            uniqueref: null,
            list: [],
            filteredOptions: [],
            selectRef: null,
            dropdownOpen: false,
            popupShowTime: 0,
            reopenedOnce: false,
        };
    },
    props: {},
    created() {
        this.uniqueref = utils.generateUUID();

        if (this.controlData.ItemFields) {
            this.displayexpn = utils.compile(this, this.controlData.ItemFields.DisplayText);
            this.titleexpn = utils.compile(this, this.controlData.ItemFields.Title);
            this.subtitleexpn = utils.compile(this, this.controlData.ItemFields.SubTitle);
            this.iconexpn = utils.compile(this, this.controlData.ItemFields.Icon);
        }
    },
    computed: {
        placeholder: function () {
            try {
                if (!this.phrenderer && this.controlData.PlaceHolder)
                    this.phrenderer = utils.compile(this, this.Translate(this.controlData.PlaceHolder));

                return this.phrenderer ? utils.evaluate(this.phrenderer, this) : '';
            }
            catch (e) {
                utils.warn('placeholder could not evaluate expression: ' + this.controlData.PlaceHolder + '; ' + e);
                return '';
            }
        },
        label: function () {
            return this.placeholder;
        },
        urlvalue: function () {
            if (!this.urlexpn && this.controlData.SourceURL)
                this.urlexpn = utils.compile(this, this.controlData.SourceURL);

            return this.urlexpn ? utils.evaluate(this.urlexpn, this) : '';
        },
        sourcevalue: function () {
            if (!this.sourceexpn && this.controlData.SourceRaw)
                this.sourceexpn = utils.compileObject(this, this.controlData.SourceRaw);

            return this.sourceexpn ? utils.evaluateObject(this.sourceexpn, this) : null;
        },
        Text: {
            get: function () {
                return this.Value;
            },
            set: function (newvalue) {
                this.Value = newvalue;
            },
        }
    },
    methods: {
        sync(v) {
            this.Value = v ?? '';

            if (v === '' || v === null || v === undefined) {
                return;
            }

            utils.debug(`AutoComplete.on-change KeyPressActions { Text:${this.Value}, Length:${this.Value.length} }`);
            utils.executeAndCompileAllActions(this.controlData.KeyPressActions,
                {
                    Text: this.Value,
                    Length: this.Value.length
                }, this);
        },
        onInputValue(val) {
            this.Value = val ?? '';
            utils.debug(`AutoComplete.on-input-value KeyPressActions { Text:${this.Value}, Length:${this.Value.length} }`);
            utils.executeAndCompileAllActions(this.controlData.KeyPressActions,
                {
                    Text: this.Value,
                    Length: this.Value.length
                }, this);
        },
        Clear() {
            this.Value = '';
            utils.debug(`AutoComplete.Clear()`);
        },
        preRenderComplete() {
            this.finishRenderHandler(this);
        },

        filterOptions(val, update, abort) {
            if (!this.controlData.FilterFields || this.controlData.FilterFields.length === 0) {
                update(() => {
                    this.filteredOptions = [...this.list];
                });
                return;
            }
            const search = (val || '').toLowerCase();
            update(() => {
                if (!search) {
                    this.filteredOptions = [...this.list];
                    return;
                }
                this.filteredOptions = this.list.filter(a => {
                    for (let i = 0; i < this.controlData.FilterFields.length; i++) {
                        const v = a.value && a.value[this.controlData.FilterFields[i]];
                        if (v && String(v).toLowerCase().includes(search))
                            return true;
                    }
                    return false;
                });
            });
        },

        async loadList() {
            let data;
            switch (this.controlData.SourceType) {
                case 'Raw':
                    data = this.controlData.SourceRaw;
                    break;

                case 'RawInterpolated':
                    data = this.sourcevalue;
                    break;

                case 'URL':
                    data = await utils.api.get(this.urlvalue);
                    break;

                default:
                    utils.warn(`AutoComplete ${this.name} SourceType of ${this.controlData.SourceType} is not implemented`);
                    return;
            }

            this.list = data.map(a => {
                this.sourceData = a;

                return {
                    text: utils.evaluate(this.displayexpn, this),
                    title: utils.evaluate(this.titleexpn, this),
                    subtitle: utils.evaluate(this.subtitleexpn, this),
                    icon: normalizeIconName(utils.evaluate(this.iconexpn, this)),
                    value: a,
                };
            });
            this.filteredOptions = [...this.list];
            utils.debug(`AutoComplete.loadList()`);
        },

        async onPopupShow() {
            this.popupShowTime = Date.now();
            this.reopenedOnce = false;
            if (this.list.length === 0) {
                await this.loadList();
            } else {
                // Defer so we don't re-render during QSelect's open; that can close the dropdown on first click.
                this.$nextTick(() => {
                    const hasFilterFields = this.controlData.FilterFields && this.controlData.FilterFields.length > 0;

                    if (this.Value && hasFilterFields) {
                        // 1. Manually run your filter logic by mocking Quasar's update/abort callbacks
                        this.filterOptions(
                            this.Value, 
                            (callback) => callback(), 
                            () => {}
                        );
                    } else {
                        // Default behavior: show all options
                        this.filteredOptions = [...this.list];
                    }
                });
            }
            this.dropdownOpen = true;
            utils.debug(`AutoComplete.showList(...)`);
        },

        onPopupHide() {
            this.dropdownOpen = false;
            // Workaround: dropdown often closes immediately on first open (same click that opened it
            // is treated as outside click). Reopen at most once if it closed within a short time.
            const elapsed = Date.now() - this.popupShowTime;
            if (elapsed < 300 && !this.reopenedOnce && this.selectRef?.showPopup) {
                this.reopenedOnce = true;
                this.$nextTick(() => {
                    this.selectRef.showPopup();
                });
            }
        },

        click(e) {
            // Defer so QSelect's dropdown open completes first; running synchronously on focus
            // can cause re-render or focus steal that closes the dropdown on first click.
            this.$nextTick(() => {
                utils.debug(`AutoComplete.click() executing ClickActions { Text:${this.Value}, Length:${this.Value.length} }`);
                utils.executeAndCompileAllActions(this.controlData.ClickActions,
                    {
                        Text: this.Value,
                        Length: this.Value.length
                    }, this);
            });
        },

        IsDropdownOpen() {
            return this.dropdownOpen;
        },

        async OpenDropdown(items, textfield, filterfields, sortfields) {
            utils.debug(`AutoComplete.OpenDropdown(...)`);
            if (items) {
                this.list = items.map(a => {
                    this.sourceData = a;
                    return {
                        text: utils.evaluate(this.displayexpn, this),
                        title: utils.evaluate(this.titleexpn, this),
                        subtitle: utils.evaluate(this.subtitleexpn, this),
                        icon: normalizeIconName(utils.evaluate(this.iconexpn, this)),
                        value: a,
                    };
                });
                this.filteredOptions = [...this.list];
            } else {
                await this.loadList();
            }
            this.$nextTick(() => {
                this.selectRef?.showPopup?.();
            });
        },

        CloseDropdown() {
            utils.debug(`AutoComplete.CloseDropdown()`);
            this.selectRef?.hidePopup?.();
        },
    },
    render() {
        if (!this.todisplay)
            return null;

        const style = {
            ...this.sizeStyle,
            ...utils.resolveStyleHints(this.styleHints, this),
            overflow: "hidden",
            whiteSpace: "nowrap",
            display: "inline-block"
        };

        const messageSlot = appSettings.DebugTranslationPrefixSetting
            ? (scope) => <TranslationContainer context={this} value={scope.message} />
            : undefined;

        const hasFilterFields = this.controlData.FilterFields && this.controlData.FilterFields.length > 0;
        const optionsSource = hasFilterFields ? this.filteredOptions : this.list;
        const hasLabel = !!this.label;

        const optionSlot = (scope) => (
            <QItem
                key={scope.opt.text}
                clickable
                v-ripple
                active={scope.selected || scope.focused}
                active-class="bg-grey-4"
                onClick={() => scope.toggleOption(scope.opt)}
            >
                <QItemSection avatar>
                    <QAvatar icon={scope.opt.icon} text-color="black" />
                </QItemSection>
                <QItemSection>
                    <QItemLabel>{scope.opt.title}</QItemLabel>
                    {scope.opt.subtitle && <QItemLabel caption>{scope.opt.subtitle}</QItemLabel>}
                </QItemSection>
            </QItem>
        );

        return (
            <div
                class={{ 'c-AutoComplete': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                style={style}
                onClick={(e) => e.stopPropagation()}
                onMousedown={(e) => e.stopPropagation()}
            >
                <QSelect
                    ref={(el) => { this.selectRef = el; }}
                    class={{
                        'q-mt-sm': true,
                        'q-mb-sm': true,
                        'c-AutoComplete-select': true,
                        'c-AutoComplete-select--no-label': !hasLabel,
                    }}
                    outlined
                    dense
                    hide-bottom-space
                    hide-dropdown-icon
                    clearable
                    autofocus
                    use-input
                    fill-input
                    hide-selected
                    newValueMode="add"
                    onBlur={() => {
                        var currentText = "";
                        if (this.selectRef && this.selectRef.$el) {
                            const inputEl = this.selectRef.$el.querySelector('input');
                            currentText = inputEl ? inputEl.value : '';
                            // Handles clicking away without pressing Enter
                            if (currentText) {
                                this.sync(currentText);
                            }
                        }
                        
                    }}
                    {...(hasLabel ? { label: this.label } : {})}
                    placeholder={this.placeholder}
                    modelValue={this.Value || null}
                    onUpdate:modelValue={(v) => this.sync(v)}
                    input-debounce={200}
                    options={optionsSource}
                    option-value="text"
                    option-label="text"
                    emit-value
                    {...(hasFilterFields ? { onFilter: this.filterOptions } : {})}
                    onPopupShow={this.onPopupShow}
                    onPopupHide={this.onPopupHide}
                    onFocus={this.click}
                    onUpdate:input-value={this.onInputValue}
                    vSlots={{
                        ...(messageSlot ? { message: messageSlot } : {}),
                        option: optionSlot
                    }}
                />
            </div>
        );
    }
});
